/**
 * Catch-all "Page not found" route.
 *
 * Why this exists
 * ===============
 * React Router v6/v7 renders `null` when no <Route> matches, and Azure
 * Static Web Apps rewrites every unmatched path to /index.html with a 200
 * (frontend/staticwebapp.config.json). Together those produced a
 * completely blank white page for ANY bad URL — no message, no console
 * error, no redirect.
 *
 * That is how the production bug of 2026-09-14 stayed invisible: proposal
 * approval emails linked to `/proposals/{quote_id}`, a path that has never
 * existed in the route table (the detail page is `/quotes/:quoteId`).
 * Recipients clicked "Review Proposal" and got a white screen. The links
 * are fixed, but the silent-blank failure mode was the deeper defect —
 * any future stale link would have failed just as quietly.
 *
 * Auth behaviour
 * ==============
 * This page renders no protected data — it is a static message — so it is
 * deliberately NOT wrapped in <ProtectedRoute>. That keeps ProtectedRoute's
 * rules fully intact for every real route: an unauthenticated hit on a
 * genuine protected path still redirects to login with `?next=` preserved.
 * Only paths that match nothing at all reach here, and they get an honest
 * 404 instead of a login bounce for a typo.
 *
 * Authenticated users see it inside the branded app shell (sidebar nav
 * intact, so they are never stranded). Signed-out users get a standalone
 * branded panel, because AppLayout requires a user.
 */
import { Link, useLocation } from "react-router-dom";
import { Warning } from "@phosphor-icons/react";
import { useAuth } from "@/lib/AuthContext";
import AppLayout from "@/components/layout/AppLayout";

// Cap the echoed path. It is attacker-controllable, and an unbounded
// echo turns a 404 into a billboard for social-engineering copy
// ("/call-1-800-... to unlock your account"). React escapes the text, so
// this is a phishing-surface limit, not an XSS one.
const MAX_ECHOED_PATH = 120;

function NotFoundBody({ signedIn }) {
  const location = useLocation();
  const attempted = `${location.pathname}${location.search || ""}`;
  const shown =
    attempted.length > MAX_ECHOED_PATH
      ? `${attempted.slice(0, MAX_ECHOED_PATH)}…`
      : attempted;

  return (
    <div className="p-6 max-w-2xl" data-testid="not-found-page">
      <div className="flex items-center gap-2 mb-3">
        <Warning size={18} weight="duotone" className="text-[#A3A3A3]" />
        <h1 className="text-[#0A0A0A] font-semibold text-base">Page not found</h1>
      </div>

      <p className="text-[#525252] text-sm mb-1">
        The link may be outdated or incorrect.
      </p>
      <p className="text-[#A3A3A3] text-sm mb-4">
        If you followed this link from an email, the page it pointed to has
        moved or no longer exists.
      </p>

      <p className="text-[#A3A3A3] text-xs mb-5">
        <span className="uppercase tracking-wider font-bold mr-1.5">Address</span>
        <code className="font-mono text-[#525252] break-all" data-testid="not-found-path">
          {shown}
        </code>
      </p>

      {signedIn ? (
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:bg-[#F5F5F5]"
          data-testid="not-found-dashboard-btn"
        >
          ← Go to Dashboard
        </Link>
      ) : (
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:bg-[#F5F5F5]"
          data-testid="not-found-signin-btn"
        >
          ← Go to Sign In
        </Link>
      )}
    </div>
  );
}

export default function NotFoundPage() {
  const { user } = useAuth();

  // AppLayout reads the signed-in user for its sidebar/header, so it can
  // only be used once we actually have one.
  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-start justify-center pt-24">
        <NotFoundBody signedIn={false} />
      </div>
    );
  }

  return (
    <AppLayout>
      <NotFoundBody signedIn />
    </AppLayout>
  );
}
